import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Tab, Tabs } from '@mui/material';
import AppLayout from '../../layout/app.layout';
import { viewDataById, deleteDataById } from '../../services/videos.service';
import ROUTE_PATH from '../../config/routes.config';
import Loading from '../../components/table-elements/loading.component';
import SnackbarInfo from '../../components/common/snackbar-info.component';
import PageNotFoundView from '../errors/not-found.view';
import ConfirmPopup from '../../components/common/confirm-popup.component';
import VideoMetadata from '../../components/content/video-metadata.component';
import VideoSeriesCategorization from '../../components/content/video-series-categorization.component';
import VideoRatingClassification from '../../components/content/video-rating-classification.component';
import VideoStatus from '../../components/content/video-status.component';
import VideoDistributorMonetization from '../../components/content/video-distributor-monetization.component';
import VideoScheduling from '../../components/content/video-scheduling.component';
import VideoSystemAdmin from '../../components/content/video-system-admin.component';
import VideoManageAssignments from '../../components/content/video-manage-assignments.component';
import VideoAdTiming from '../../components/content/video-ad-timing.component';
import VideoEditorial from '../../components/content/video-editorial.component';
import VideoAdmin from '../../components/content/video-admin.component';
import VideoSubtitles from '../../components/content/video-subtitles.component';
import Iconify from '../../components/common/iconify.component';
import replaceIdUrl from '../../utils/url.util';

const TABS_CONFIG = [
  { value: 'metadata', label: 'Profile', component: VideoMetadata },
  {
    value: 'series',
    label: 'Series',
    component: VideoSeriesCategorization,
  },
  {
    value: 'systemAdmin',
    label: 'Assets',
    component: VideoSystemAdmin,
  },
  {
    value: 'distributor',
    label: 'Owner',
    component: VideoDistributorMonetization,
  },
  {
    value: 'scheduling',
    label: 'Rights Management',
    component: VideoScheduling,
  },
  { value: 'adTiming', label: 'Ad Timing', component: VideoAdTiming },
  { value: 'editorial', label: 'Editorial', component: VideoEditorial },
  {
    value: 'rating',
    label: 'Rating',
    component: VideoRatingClassification,
  },
  {
    value: 'assignments',
    label: 'Assignment',
    component: VideoManageAssignments,
  },
  { value: 'status', label: 'Status', component: VideoStatus },
  { value: 'admin', label: 'Admin', component: VideoAdmin },
  {
    value: 'subtitles',
    label: 'Subtitles',
    component: VideoSubtitles,
  },
  // { value: 'imdb', label: 'IMDB Settings', component: VideoImdbSettings },
  // { value: 'others', label: 'Others', component: VideoOthers },
];

const VideoEditView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(TABS_CONFIG[0].value);
  const [loading, setLoading] = useState(true);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [showPageNotFound, setShowPageNotFound] = useState(false);
  const [videoDetail, setVideoDetail] = useState({});
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [hasFormValueChanged, setHasFormValueChanged] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const tabValueRef = useRef('');

  const formValueChangedCallback = (val) => {
    setHasFormValueChanged(val);
  };

  const handleRefreshData = () => {
    setReloadCounter(reloadCounter + 1);
  };

  const handleTabSwitchSuccess = () => {
    setTabValue(tabValueRef.current);
    setHasFormValueChanged(false);
    setShowWarning(false);
  };

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteDataById(id)
      .then(() => {
        setShowDeleteConfirmation(false);
        navigate(ROUTE_PATH.VIDEOS);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setShowDeleteConfirmation(false);
      });
  };

  useEffect(() => {
    viewDataById(id)
      .then((res) => {
        setVideoDetail(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response.status === 404) {
          setShowPageNotFound(true);
          return;
        }
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [id, reloadCounter]);

  return Number.isNaN(Number(id)) || showPageNotFound ? (
    <PageNotFoundView />
  ) : (
    <AppLayout
      pageTitle={`Edit Video Details - ${videoDetail.title}`}
      showBack
      handleBack={() => navigate(ROUTE_PATH.VIDEOS)}
    >
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="flex-end"
        my={1}
      >
        <Button
          type="button"
          color="primary"
          variant="contained"
          onClick={() => setShowDeleteConfirmation(true)}
          disabled={loading}
          sx={{ mr: 1 }}
        >
          Delete
        </Button>
        <Button
          color="inherit"
          variant="contained"
          startIcon={<Iconify icon="lets-icons:view" />}
          onClick={() => {
            const url = replaceIdUrl(ROUTE_PATH.VIDEO_VIEW_DETAILS, id);
            navigate(url);
          }}
          disabled={loading}
          sx={{ mr: 1 }}
        >
          View Video Details
        </Button>
        <Button
          color="inherit"
          variant="contained"
          startIcon={<Iconify icon="ic:twotone-refresh" />}
          onClick={() => handleRefreshData()}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Box height="100%" display="flex">
        <Tabs
          value={tabValue}
          aria-label="tabs"
          orientation="vertical"
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="transparent"
          sx={{ minWidth: 150, maxWidth: 150 }}
          onChange={(_, v) => {
            if (hasFormValueChanged) {
              tabValueRef.current = v;
              setShowWarning(true);
            } else {
              setTabValue(v);
            }
          }}
        >
          {TABS_CONFIG.map((tab, index) => (
            <Tab
              key={index + 1}
              value={tab.value}
              label={tab.label}
              sx={{
                background: tabValue === tab.value && '#fff',
                borderLeft: tabValue === tab.value && '2px solid #FD0100',
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
            />
          ))}
        </Tabs>

        <Box flexGrow={1}>
          {loading && <Loading />}

          {!loading && (
            <>
              {TABS_CONFIG.map(({ value, component: Component }) =>
                tabValue === value ? (
                  <Component
                    key={value}
                    videoDetail={videoDetail}
                    type={tabValue}
                    handleRefreshData={handleRefreshData}
                    formValueChangedCallback={formValueChangedCallback}
                  />
                ) : null
              )}
            </>
          )}
        </Box>
      </Box>

      {showWarning && (
        <ConfirmPopup
          title="Tab Switch Warning"
          message="Do you want to switch tab without saving data? Before switching tab, save your changes otherwise you lost all your changes and can not undo this action!"
          onCancel={() => setShowWarning(false)}
          onSuccess={() => handleTabSwitchSuccess()}
        />
      )}

      {showDeleteConfirmation && (
        <ConfirmPopup
          title="Delete Video"
          message={`Do you want to delete ${videoDetail.title} video? You can not undo this action!`}
          onCancel={() => setShowDeleteConfirmation(false)}
          onSuccess={handleDeleteData}
          successButtonText="Delete"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="delete"
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </AppLayout>
  );
};

export default VideoEditView;
